import { createSlice, PayloadAction, createAsyncThunk, nanoid } from '@reduxjs/toolkit';
import { getConsultation } from '../../../virtco/services/data-provider';
import { logout } from '../../../redux/action/logout-action';

interface Image {
  id: string;
  url: string;
  isCoverImage: boolean;
}

interface BasicDetailsSnapshot {
  title: string;
  description: string;
  images: Image[];
}

interface BasicDetailsState {
  id: string | null;
  title: string;
  description: string;
  images: Image[];
  /** Last saved / server-loaded snapshot for change detection across step navigation. */
  baseline: BasicDetailsSnapshot | null;
  /** Async fetch state for basic details slice (not consultation PUBLISHED/DRAFT). */
  status: 'idle' | 'loading' | 'succeeded' | 'failed' | 'published';
  /** Consultation service status from API (e.g. PUBLISHED, DRAFT) — see myconsultation ConsultationCard.status */
  consultationServiceStatus: string | null;
  /** True when consultation was created via AI flow — unlocks Generate with AI image prompt. */
  isAICreated: boolean;
  /** AI-generated image prompt from GetDetails — shown in Generate with AI dialog. */
  bannerImagePrompt: string | null;
}

const initialState: BasicDetailsState = {
  id: null,
  title: '',
  description: '',
  images: [],
  baseline: null,
  status: 'idle',
  consultationServiceStatus: null,
  isAICreated: false,
  bannerImagePrompt: null,
};

const resolveIsAICreated = (data: any): boolean =>
  data?.isAICreated === true || data?.IsAICreated === true;

const resolveBannerImagePrompt = (data: any): string | null => {
  const prompt = data?.bannerImagePrompt ?? data?.BannerImagePrompt;
  if (typeof prompt !== 'string') return null;
  const trimmed = prompt.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const snapshotFromPayload = (payload: {
  title: string;
  description: string;
  images: Image[];
}): BasicDetailsSnapshot => ({
  title: payload.title,
  description: payload.description,
  images: payload.images.map((img) => ({ ...img })),
});

const hasUnsavedBasicDetailsEdits = (state: BasicDetailsState): boolean => {
  if (!state.baseline) return false;
  if (state.title !== state.baseline.title) return true;
  if (state.description !== state.baseline.description) return true;
  if (state.images.length !== state.baseline.images.length) return true;
  for (let i = 0; i < state.images.length; i++) {
    const current = state.images[i];
    const base = state.baseline.images[i];
    if (!base || current.url !== base.url || current.isCoverImage !== base.isCoverImage) {
      return true;
    }
  }
  return false;
};

export type FetchBasicDetailsArg =
  | string
  | {
      id: string;
      /** When true, overwrite local unsaved edits (e.g. re-opening edit after leaving). */
      force?: boolean;
    };

const getFetchBasicDetailsMeta = (arg: FetchBasicDetailsArg) => ({
  consultationId: typeof arg === 'string' ? arg : arg.id,
  force: typeof arg === 'object' && !!arg.force,
});

export const fetchBasicDetails = createAsyncThunk(
  'basicDetails/fetchBasicDetails',
  async (arg: FetchBasicDetailsArg) => {
    const { consultationId } = getFetchBasicDetailsMeta(arg);
    const response = await getConsultation(consultationId);
    const data = response.data?.data;
    let images: Image[] = [];
    const coverImageUrl = data?.imageUrl || data?.coverImageUrl;
    if (coverImageUrl) {
      images.push({ id: nanoid(), url: coverImageUrl, isCoverImage: true });
    }
    const galleryData = data?.consultationGallery || data?.userGalleryDtos || [];
    if (galleryData && galleryData.length > 0) {
      galleryData.forEach((galleryItem: any) => {
        if (galleryItem.url !== coverImageUrl) {
          images.push({
            id: galleryItem.id || nanoid(),
            url: galleryItem.url,
            isCoverImage: galleryItem.isCoverImage || false,
          });
        }
      });
    }
    return {
      id: data?.id || null,
      title: data?.title || '',
      description: data?.description || '',
      images,
      consultationServiceStatus:
        typeof data?.status === 'string' ? data.status : null,
      isAICreated: resolveIsAICreated(data),
      bannerImagePrompt: resolveBannerImagePrompt(data),
    };
  }
);

const basicDetailsSlice = createSlice({
  name: 'basicDetails',
  initialState,
  reducers: {
    setBasicDetails: (state, action: PayloadAction<Partial<BasicDetailsState>>) => {
      return { ...state, ...action.payload };
    },
    setData(state, action: PayloadAction<{ id?: string; title: string; description: string; images: Image[]; status?: string }>) {
      state.id = action.payload.id || null;
      state.title = action.payload.title;
      state.description = action.payload.description;
      state.images = action.payload.images;
      if (action.payload.status) {
        state.status = action.payload.status as any;
      }
    },
    clearData(state) {
      state.id = null;
      state.title = '';
      state.description = '';
      state.images = [];
      state.baseline = null;
      state.status = 'idle';
      state.consultationServiceStatus = null;
      state.isAICreated = false;
      state.bannerImagePrompt = null;
    },
    setBasicDetailsBaseline(state) {
      state.baseline = snapshotFromPayload(state);
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchBasicDetails.pending, (state, action) => {
        const { consultationId } = getFetchBasicDetailsMeta(action.meta.arg);
        if (state.id !== consultationId) {
          state.id = null;
          state.title = '';
          state.description = '';
          state.images = [];
          state.baseline = null;
          state.consultationServiceStatus = null;
          state.isAICreated = false;
          state.bannerImagePrompt = null;
        }
        state.status = 'loading';
      })
      .addCase(fetchBasicDetails.fulfilled, (state, action) => {
        const { force } = getFetchBasicDetailsMeta(action.meta.arg);
        state.status = 'succeeded';
        if (
          !force &&
          hasUnsavedBasicDetailsEdits(state) &&
          state.id === action.payload.id
        ) {
          state.consultationServiceStatus =
            action.payload.consultationServiceStatus ?? state.consultationServiceStatus;
          state.isAICreated = action.payload.isAICreated;
          state.bannerImagePrompt = action.payload.bannerImagePrompt;
          return;
        }
        state.id = action.payload.id;
        state.title = action.payload.title;
        state.description = action.payload.description;
        state.images = action.payload.images;
        state.baseline = snapshotFromPayload(action.payload);
        state.consultationServiceStatus =
          action.payload.consultationServiceStatus ?? null;
        state.isAICreated = action.payload.isAICreated;
        state.bannerImagePrompt = action.payload.bannerImagePrompt;
      })
      .addCase(fetchBasicDetails.rejected, state => {
        state.status = 'failed';
      })
      .addCase(logout, () => initialState);
  },
});

export const { setBasicDetails, setData, clearData, setBasicDetailsBaseline } = basicDetailsSlice.actions;
export default basicDetailsSlice.reducer;

// Selector for consultationId and status
export const selectConsultationId = (state: any) => state.basicDetails.id;
export const selectConsultationStatus = (state: any) => state.basicDetails.status;
export const selectConsultationServiceStatus = (state: any) =>
  state.basicDetails.consultationServiceStatus; 